import { BuilderContent } from '@builder.io/sdk';
import { Faq } from '@/components/ui/HelpCentre/HelpCentre';

interface SchemaOptions {
  content?: BuilderContent;
  url: string;
  model?: string;
}

const baseDescription =
  'Tempcover is a UK-based insurtech company offering flexible, short-term vehicle insurance policies ranging from 1 hour to 28 days. Ideal for drivers needing temporary coverage for cars, vans, and more.';

const schemaParts = {
  author: {
    '@type': 'Organization',
    name: 'Tempcover',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Tempcover',
    logo: 'https://www.tempcover.com/icon-180x180.png',
    sameAs: [
      'https://www.facebook.com/Tempcover',
      'https://twitter.com/Tempcover',
      'https://www.linkedin.com/company/tempcover',
    ],
  },
  inLanguage: 'en-GB',
};

function getSchemaType({ content, url, model }: SchemaOptions) {
  const schemaTypes = {
    page: {
      '@type': 'WebPage',
      '@id': `https://www.tempcover.com/${url}`,
      url: `https://www.tempcover.com/${url}`,
      name: content?.data?.title ?? content?.data?.name ?? '',
      description: content?.data?.description ?? '',
      inLanguage: schemaParts.inLanguage,
      datePublished: content?.firstPublished ?? '',
      dateModified: content?.lastUpdated ?? '',
    },
    article: {
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
      headline: content?.data?.title ?? '',
      description: content?.data?.description ?? '',
      inLanguage: schemaParts.inLanguage,
      image: content?.data?.heroImage ?? '',
      author: schemaParts.author,
      publisher: schemaParts.publisher,
      datePublished: content?.firstPublished ?? '',
      dateModified: content?.lastUpdated ?? '',
    },
    about: {
      '@type': 'AboutPage',
      url: 'https://www.tempcover.com/about-us',
      mainEntity: {
        '@type': 'Organization',
        name: 'Tempcover Ltd',
        url: 'https://www.tempcover.com',
        logo: 'https://www.tempcover.com/icon-180x180.png',
        description: baseDescription,
        foundingDate: '2006-01-01',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Admiral House, Harlington Way',
          addressLocality: 'Fleet',
          addressRegion: 'Hampshire',
          postalCode: 'GU51 4BB',
          addressCountry: 'GB',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+44 330 460 9604',
          contactType: 'Customer Service',
          areaServed: 'GB',
          availableLanguage: 'English',
        },
        sameAs: [
          'https://www.facebook.com/Tempcover',
          'https://twitter.com/Tempcover',
          'https://www.linkedin.com/company/tempcover',
        ],
      },
    },
    contact: {
      '@type': 'ContactPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'Tempcover Ltd',
        url: 'https://www.tempcover.com/contact-us',
        logo: 'https://www.tempcover.com/icon-180x180.png',
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: '+44 330 460 9604',
            contactType: 'Customer Service',
            areaServed: 'GB',
            availableLanguage: 'English',
            hoursAvailable: ['Mo-Fr 07:00-21:00', 'Sa-Su 09:00-17:30'],
          },
          {
            '@type': 'ContactPoint',
            telephone: '+44 330 460 9633',
            contactType: 'Legal',
            areaServed: 'GB',
            availableLanguage: 'English',
          },
          {
            '@type': 'ContactPoint',
            name: 'Live Chat',
            url: 'https://www.tempcover.com/contact-us',
            availableLanguage: 'English',
          },
        ],
        sameAs: [
          'https://www.facebook.com/Tempcover',
          'https://twitter.com/Tempcover',
          'https://www.linkedin.com/company/tempcover',
        ],
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Admiral House, Harlington Way',
          addressLocality: 'Fleet',
          addressRegion: 'Hampshire',
          postalCode: 'GU51 4BB',
          addressCountry: 'GB',
        },
        email: 'mailto:contactus@tempcover.com',
      },
    },
    // For FAQs included in model. Component-level FAQs hava schema defined locally
    faq: {
      '@type': 'FAQPage',
      mainEntity: content?.data?.faqs
        ? content.data.faqs.slice(0, 2).map((faq: Faq) => ({
            '@type': 'Question',
            name: faq.title,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.content,
            },
          }))
        : [],
    },
    product: {
      '@type': 'Product',
      name: content?.data?.title ?? '',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: '43221', // TODO: fetch these values dynamically
        bestRating: '5',
        worstRating: '1',
      },
    },
  };

  if (model === 'article') return schemaTypes.article;
  if (model === 'help-centre') return schemaTypes.faq;
  if (url.match(/^\/?about-us/)) return schemaTypes.about;
  if (url.match(/^\/?contact-us/)) return schemaTypes.contact;
  if (url.match(/^\/?help-centre\/[a-zA-z\-]+/)) return schemaTypes.faq;
  if (content?.data?.isProductPage) return schemaTypes.product;

  return schemaTypes.page;
}

export default function generateSchema(options: SchemaOptions) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': 'https://www.tempcover.com/#website',
        url: 'https://www.tempcover.com',
        name: 'Tempcover',
        image: 'https://www.tempcover.com/builder-home-rebrand/favicon.ico',
        description: baseDescription,
        inLanguage: schemaParts.inLanguage,
        publisher: schemaParts.publisher,
      },
      getSchemaType(options),
    ],
  };
}
